import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyGetAttachedPreviewFileHtmlQuery } from '@core/app/api/profileApi'
import { useFilePreview } from '@core/utils/contexts/FilePreviewContext/FilePreviewContext'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import useHtmlPrint from '@core/utils/hooks/useHtmlPrint'

const baseClass = 'FilePreviewWindow'

function FileRecDocumentPreview() {
    const { getTitle } = useApplyV2NavTranslations()
    const { previewFile, isOpen, closePreview, getFileName, fileExtension, folder } = useFilePreview()

    const { handlePrint } = useHtmlPrint()

    const containerRef = useRef<HTMLDivElement>(null)
    const [zoom, setZoom] = useState(100)
    const [isFitToScreen, setIsFitToScreen] = useState(false)

    const [testBinaryRequest, { isLoading: isBinaryLoading }] = useLazyGetAttachedPreviewFileHtmlQuery()
    const [htmlBody, setHtmlBody] = useState<string | null>(null)
    const [prevOverflow, setPrevOverflow] = useState<string | null>(document.body.style.overflow)

    useEffect(() => {
        const loadImage = async () => {
            if (!previewFile?.CandidateFileID) {
                return
            }

            try {
                const response = await testBinaryRequest({
                    candidateFileId: previewFile.CandidateFileID,
                }).unwrap()
                if (response?.data?.body) {
                    setHtmlBody(response.data.body)
                }
            } catch (e) {
                console.error('Download file error:', e)
            }
        }

        loadImage()
    }, [isOpen, previewFile, testBinaryRequest])

    const handleZoomChange = (value: 'decrease' | 'increase') => {
        if (value === 'decrease') {
            setZoom((prev) => {
                if (prev > 200) {
                    setIsFitToScreen(false)
                    return 200
                }
                return Math.max(25, prev - 25)
            })
        } else {
            setZoom((prev) => Math.min(200, prev + 25))
        }
    }

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closePreview()
            }
            const isModifierPressed = e.ctrlKey || e.metaKey

            if (isModifierPressed) {
                if (e.key === '=' || e.key === '+') {
                    e.preventDefault()
                    handleZoomChange('increase')
                }

                if (e.key === '-' || e.key === '_') {
                    e.preventDefault()
                    handleZoomChange('decrease')
                }

                if (e.key === '0') {
                    e.preventDefault()
                    setZoom(100)
                }
            }
        },
        [closePreview]
    )

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = prevOverflow || 'unset'
        }
    }, [isOpen, handleKeyDown])

    const lastModified = (() => {
        if (previewFile && 'lastModified' in previewFile) {
            return previewFile.lastModified || 0
        } else if (previewFile && 'uploaded' in previewFile) {
            const uploaded = Date.parse(previewFile.uploaded ?? '')
            return isNaN(uploaded) ? 0 : uploaded
        }
        return 0
    })()

    const formattedDate = lastModified
        ? new Date(lastModified).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : ''

    const isLoadingSpinner = useMemo(() => {
        return isOpen && isBinaryLoading
    }, [isOpen, isBinaryLoading])

    const imageStyle = useMemo(() => {
        const scaledWidth = 800 * (zoom / 100)
        // const scaledHeight = imageDimensions.height * (zoom / 100)

        return {
            width: isFitToScreen ? `calc(100% - 36px)` : `${scaledWidth}px`,
            height: 'auto',
            maxWidth: '100%',
            objectFit: 'contain' as const,
            fontSize: isFitToScreen ? '24px' : `${16 * (zoom / 100)}px`,
        }
    }, [zoom, isFitToScreen])

    if (!previewFile) return null

    return (
        <div className={`${baseClass} ${isOpen ? `${baseClass}--open` : ''}`}>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__left`}>
                    <div className={`${baseClass}__header__left__name`}>{getFileName()}</div>
                    <div className={`${baseClass}__header__left__date`}>{formattedDate}</div>
                </div>
                {/*<div className={`${baseClass}__header__center`}>*/}
                {/*    <div className={`${baseClass}__header__center__page`}>*/}
                {/*        <span className={`${baseClass}__page-indicator`}>*/}
                {/*            {currentPage} / {totalPages}*/}
                {/*        </span>*/}
                {/*    </div>*/}
                {/*</div>*/}
                <div className={`${baseClass}__header__right`}>
                    <div className={`${baseClass}__zoom`}>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-decrease"
                            onClick={() => {
                                handleZoomChange('decrease')
                            }}
                        >
                            <span>–</span>
                        </button>
                        <span>{zoom}%</span>
                        <button
                            className={`${baseClass}__button ${baseClass}__zoom__button`}
                            aria-label="zoom-increase"
                            onClick={() => {
                                handleZoomChange('increase')
                            }}
                            disabled={zoom >= 200}
                        >
                            <span>+</span>
                        </button>
                    </div>
                    <div className={`${baseClass}__header__right__nav`}>
                        {!isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(true)

                                    setZoom(300)
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Expand-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M14.75.85a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 1 0 1.272 1.272l6.964-6.963V9.25a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9zm-5.5 22.3a.9.9 0 1 0 0-1.8H3.923l6.963-6.964a.9.9 0 1 0-1.272-1.272L2.65 20.077V14.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Expand-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        {isFitToScreen && (
                            <button
                                className={`${baseClass}__button`}
                                aria-label="expand"
                                onClick={() => {
                                    setIsFitToScreen(false)
                                    setZoom(100)
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <g clipPath="url(#Icon-Collapse-Regular_svg__a)">
                                        <path
                                            fillRule="evenodd"
                                            d="M21.25 11.15a.9.9 0 1 0 0-1.8h-5.327l6.964-6.964a.9.9 0 1 0-1.273-1.272L14.65 8.077V2.75a.9.9 0 1 0-1.8 0v7.5a.9.9 0 0 0 .9.9zm-18.5 1.7a.9.9 0 1 0 0 1.8h5.327l-6.963 6.964a.9.9 0 0 0 1.272 1.273l6.964-6.964v5.327a.9.9 0 1 0 1.8 0v-7.5a.9.9 0 0 0-.9-.9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </g>
                                    <defs>
                                        <clipPath id="Icon-Collapse-Regular_svg__a">
                                            <path d="M0 0h24v24H0z"></path>
                                        </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        <button
                            className={`${baseClass}__button`}
                            aria-label="print"
                            onClick={() => handlePrint(htmlBody || '')}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="1em"
                                height="1em"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M6.94 2.9a.1.1 0 0 0-.1.1v2.975h10.32V3a.1.1 0 0 0-.1-.1zm12.02 3.075V3a1.9 1.9 0 0 0-1.9-1.9H6.94A1.9 1.9 0 0 0 5.04 3v2.975H3a1.9 1.9 0 0 0-1.9 1.9v10.168c0 1.05.85 1.9 1.9 1.9h2.04V21c0 1.05.85 1.9 1.9 1.9h10.12a1.9 1.9 0 0 0 1.9-1.9v-1.057h1.828c1.062 0 2.112-.786 2.112-1.975V7.875a1.9 1.9 0 0 0-1.9-1.9zM3 7.775a.1.1 0 0 0-.1.1v10.168a.1.1 0 0 0 .1.1h2.04V16.28c0-1.05.85-1.9 1.9-1.9h10.12c1.05 0 1.9.85 1.9 1.9v1.863h1.828c.12 0 .209-.043.26-.088.05-.044.052-.076.052-.087V7.875a.1.1 0 0 0-.1-.1zm3.94 8.405a.1.1 0 0 0-.1.1V21a.1.1 0 0 0 .1.1h10.12a.1.1 0 0 0 .1-.1v-4.72a.1.1 0 0 0-.1-.1z"
                                    clipRule="evenodd"
                                ></path>
                            </svg>
                            <span>{getTitle('1021', 'Print')}</span>
                        </button>
                    </div>
                </div>
                <button
                    className={`${baseClass}__button ${baseClass}__close-button`}
                    onClick={closePreview}
                    aria-label="Close preview"
                >
                    <span>✕</span>
                </button>
            </div>
            <div
                className={`${baseClass}__content`}
                ref={containerRef}
                // onScroll={() => {
                //     updateCurrentPage()
                // }}
            >
                {isLoadingSpinner && <div className={`${baseClass}__content__spinner`} />}

                {htmlBody && (
                    <div
                        className={`${baseClass}__html-container`}
                        style={imageStyle}
                        dangerouslySetInnerHTML={{ __html: htmlBody }}
                    ></div>
                )}
            </div>
        </div>
    )
}

export default FileRecDocumentPreview
